import React, { useEffect, useRef, useState } from 'react';
import { diffWords } from 'diff';
import { useDraggable } from './hooks/useDraggable';
import { adjustPositionToViewport } from './utils/viewportUtils';
import './DiffView.css';

// Reusable component for rendering diff text
export function DiffText({ originalText, newText, className = '' }) {
  if (!originalText || !newText) return null;

  const diff = diffWords(originalText, newText);
  return (
    <span className={className}>
      {diff.map((part, index) => {
        const diffClass = part.added
          ? 'diff-added'
          : part.removed
          ? 'diff-removed'
          : 'diff-unchanged';

        return (
          <span key={index} className={diffClass}>
            {part.value}
          </span>
        );
      })}
    </span>
  );
}

export default function DiffView({
  originalText,
  newText,
  onAccept,
  onReject,
  position,
  isStreaming = false
}) {
  const containerRef = useRef(null);
  const [adjustedPosition, setAdjustedPosition] = useState(position);

  // Use the custom draggable hook
  const { isDragging, handleDragStart, userHasDragged } = useDraggable(
    adjustedPosition,
    setAdjustedPosition,
    containerRef,
    {
      bounds: { left: 10, top: 10, right: 10, bottom: 10 }
    }
  );

  useEffect(() => {
    // Only reset position if user hasn't manually dragged it
    if (position && !isDragging && !userHasDragged) {
      setAdjustedPosition(position);
    }
  }, [position, isDragging, userHasDragged]);

  // Adjust position to keep within viewport
  useEffect(() => {
    if (containerRef.current && adjustedPosition && !isDragging) {
      const container = containerRef.current;
      const rect = container.getBoundingClientRect();

      const newPosition = adjustPositionToViewport(adjustedPosition, rect, {
        margin: 10,
        allowFlip: true
      });

      // Only update if position changed
      if (newPosition.x !== adjustedPosition.x || newPosition.y !== adjustedPosition.y) {
        setAdjustedPosition(newPosition);
      }
    }
  }, [adjustedPosition, isDragging]);


  // Handle keyboard shortcuts
  useEffect(() => {
    if (!originalText || !newText) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onReject();
      } else if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
        onAccept();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [originalText, newText, onAccept, onReject]);

  if (!originalText || !adjustedPosition) return null;

  return (
    <div
      ref={containerRef}
      className={`diff-view-container ${isDragging ? 'dragging' : ''}`}
      style={{
        left: `${adjustedPosition.x}px`,
        top: `${adjustedPosition.y}px`,
      }}
    >
      <div 
        className="diff-view-header"
        onMouseDown={handleDragStart}
      >
        <div className="diff-view-header-content">
          <span className="drag-handle">⋮⋮</span>
          <div>
            <span className="diff-view-title">
              {isStreaming ? '⚡ Generating...' : '✨ Proposed Changes'}
            </span>
            <span className="diff-view-subtitle">
              {isStreaming ? 'Streaming response' : 'Review the changes below'}
            </span>
          </div>
        </div>
        <button 
          onClick={onReject} 
          onMouseDown={(e) => e.stopPropagation()} 
          className="diff-close-button" 
          title="Close (Esc)"
        >
          ✕
        </button>
      </div>

      <div className="diff-view-content">
        {isStreaming ? (
          // Show streaming text without diff
          <div className="streaming-text">
            {newText || <span className="streaming-cursor">▋</span>}
          </div>
        ) : !isStreaming && newText ? (
          // Show diff when streaming is complete
          <DiffText originalText={originalText} newText={newText} />
        ) : null}
      </div>

      {!isStreaming && (
        <div className="diff-view-actions">
          <button
            onClick={onReject}
            className="diff-action-button reject"
            title="Reject changes (Esc)"
          >
            <span className="action-icon">✕</span>
            <span className="action-label">Reject</span>
          </button>
          <button
            onClick={onAccept}
            className="diff-action-button accept"
            title="Accept changes (Cmd/Ctrl+Enter)"
          >
            <span className="action-icon">✓</span>
            <span className="action-label">Accept</span>
          </button>
        </div>
      )}
    </div>
  );
}

